/**
 * Permission checking logic — extracted for testability.
 *
 * Supports granular keys (e.g. 'clients_view') and legacy backwards-compat
 * where old role had 'clients: true' → grants clients_view, clients_edit, clients_delete.
 */

export const LEGACY_MAP = {
  clients:  ['clients_view', 'clients_edit', 'clients_delete'],
  staff:    ['staff_view', 'staff_edit'],
  services: ['services_view', 'services_edit'],
  calendar: ['calendar_view', 'calendar_create', 'calendar_edit'],
  finances: ['finances_view'],
  // Renamed keys — old saved roles still work
  categories:         ['clients_categories'],
  schedule:           ['schedule_view'],
  schedule_templates: ['schedule_edit'],
  tariff_plans:       ['settings_tariffs'],
  reports:            ['reports_view'],
  salary_calc:        ['salary_view'],
  salary_schemes:     ['salary_edit'],
  inventory_products: ['inventory_view'],
  inventory_stock:    ['inventory_view'],
  orders_create:      ['orders_create', 'pos_sell'],
  finances_create:    ['finances_view'],
};

/**
 * Check if a role has a specific permission.
 * @param {Object|null} role - Role object with `permissions` map
 * @param {string} key - Permission key to check
 * @returns {boolean}
 */
export function hasPermission(role, key) {
  if (!role || !role.permissions) return false;
  const p = role.permissions;
  // Стара назва / id пункту меню, за якою стоять сучасні права
  // (inventory_products → inventory_view). Сама собі не псевдонім
  // (orders_create є і старою, і сучасною назвою).
  const granular = LEGACY_MAP[key];
  const isAlias = !!granular && !granular.includes(key);
  // Direct match (new granular key). Для псевдоніма — лише коли true:
  // застаріле false не має перекривати сучасне право, видане на сторінці ролей.
  if (p[key] !== undefined && (!isAlias || p[key])) return !!p[key];
  // Legacy: old role has "clients: true" → grant clients_view, clients_edit, etc.
  for (const [legacyKey, granularKeys] of Object.entries(LEGACY_MAP)) {
    if (granularKeys.includes(key) && p[legacyKey]) return true;
  }
  // І навпаки: питають стару назву, а роль має сучасні права. Системна роль
  // admin будується з каталогу сучасних прав і старих назв не містить —
  // меню ховало від адміністратора «Номенклатуру», «Залишки», «Графік»,
  // «Звіти», «Тарифні плани», зарплату й категорії клієнтів (29.09, 05.10).
  if (isAlias) return granular.every(g => !!p[g]);
  return false;
}
